import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Folder, Users } from 'lucide-react';
import { Button, Input, Modal } from '../common';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { ProjectMembersList } from './ProjectMembersList';
import type { CreateProjectRequest } from '../../types';

interface ProjectFormData {
  name: string;
  description: string;
  color: string;
}

const PROJECT_COLORS = [
  '#4F46E5', '#7C3AED', '#DB2777', '#DC2626',
  '#EA580C', '#D97706', '#65A30D', '#16A34A',
  '#0D9488', '#0284C7', '#6366F1', '#6B7280',
];

type TabType = 'details' | 'members';

/**
 * Modal for creating/editing projects.
 * When editing, shows tabs for project details and member management.
 */
export const ProjectForm: React.FC = () => {
  const { t } = useTranslation();
  const { projectModalOpen, editingProjectId, closeProjectModal } = useUIStore();
  const { currentProject, createProject, updateProject, fetchProject, clearCurrentProject, isLoading } = useProjectStore();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('details');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProjectFormData>();

  const selectedColor = watch('color', '#4F46E5');

  // Reset tab when modal opens/closes
  useEffect(() => {
    if (projectModalOpen) {
      setActiveTab('details');
      if (editingProjectId) {
        fetchProject(editingProjectId);
      } else {
        reset({
          name: '',
          description: '',
          color: '#4F46E5',
        });
      }
    } else {
      clearCurrentProject();
    }
  }, [projectModalOpen, editingProjectId, fetchProject, reset, clearCurrentProject]);

  useEffect(() => {
    if (currentProject && editingProjectId) {
      reset({
        name: currentProject.name,
        description: currentProject.description || '',
        color: currentProject.color,
      });
    }
  }, [currentProject, editingProjectId, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    const projectData: CreateProjectRequest = {
      name: data.name,
      description: data.description || undefined,
      color: data.color,
    };

    if (editingProjectId) {
      await updateProject(editingProjectId, projectData);
    } else {
      await createProject(projectData);
    }

    closeProjectModal();
  };

  // Project owners and org admins can manage members
  const isEditing = !!editingProjectId;
  const isOrgAdmin = user?.organizationRoles?.find(r => r.orgId === user.activeOrganizationId)?.role === 'ADMIN';
  const isProjectOwner = currentProject ? (currentProject.isOwner ?? currentProject.ownerId === user?.id) : false;

  // Show members tab to project owners and org admins
  const canManageMembers = isProjectOwner || isOrgAdmin;

  return (
    <Modal
      isOpen={projectModalOpen}
      onClose={closeProjectModal}
      title={isEditing ? t('common.edit') : t('projects.form.newProject')}
      size="md"
    >
      {/* Tabs - Only show when editing */}
      {isEditing && (
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`
              flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors
              ${activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }
            `}
          >
            <Folder className="w-4 h-4" />
            Details
          </button>
          {canManageMembers && (
            <button
              type="button"
              onClick={() => setActiveTab('members')}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors
                ${activeTab === 'members'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }
              `}
            >
              <Users className="w-4 h-4" />
              Members
              {currentProject?.memberCount && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700">
                  {currentProject.memberCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Details Tab */}
      {(!isEditing || activeTab === 'details') && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label={t('projects.form.name')}
            placeholder={t('projects.form.namePlaceholder')}
            error={errors.name?.message}
            {...register('name', { required: t('projects.form.nameRequired') })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder={t('projects.form.descriptionPlaceholder')}
              {...register('description')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {PROJECT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setValue('color', color)}
                  className={`
                    w-8 h-8 rounded-lg transition-all
                    ${selectedColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'}
                  `}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input type="hidden" {...register('color')} />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={closeProjectModal} className="w-full sm:w-auto min-h-[48px] sm:min-h-0">
              {t('common.cancel')}
            </Button>
            {/* Show Save Changes for project owners and org admins when editing */}
            {(!isEditing || isProjectOwner || isOrgAdmin) && (
              <Button type="submit" isLoading={isLoading} className="w-full sm:w-auto min-h-[48px] sm:min-h-0">
                {isEditing ? t('projects.form.saveChanges') : t('projects.form.createProject')}
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Members Tab - Show for project owners and org admins */}
      {isEditing && activeTab === 'members' && canManageMembers && currentProject && (
        <ProjectMembersList
          projectId={currentProject.id}
          isOwner={isProjectOwner}
        />
      )}
    </Modal>
  );
};

export default ProjectForm;